import { Flex } from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { AudioPlayer } from '~/components/shared/AudioPlayer'
import { AudioPlayerVariant } from '~/components/shared/componentStyles/AudioPlayer.style'
import { CallDetailsOverviewTab } from '~/pages/Calls/CallDetails/CallDetailsOverviewTab'
import type { MargoSummaryViewModel } from '~/pages/Calls/CallDetails/MargoSummary/MargoSummary.types'
import { useGetVoicemail } from '~/queries/voicemailQueries'

import { getTimeDifferenceInMs } from '@mangovoice/core'
import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'
import { CallInstance } from '@mangovoice/types'

import { bounceAnimation, BouncingEllipsis } from './BouncingEllipsis'

interface CallDetailsBasicContentProps {
  callInstance: CallInstance
  margoSummary?: MargoSummaryViewModel
  isModal?: boolean
  requestedTimestamp: number | undefined
  playbackTimestampRef: React.MutableRefObject<number | undefined>
}

export const CallDetailsBasicContent = ({
  callInstance,
  margoSummary,
  isModal = false,
  requestedTimestamp,
  playbackTimestampRef,
}: CallDetailsBasicContentProps) => {
  const { t } = useTranslation()
  const scrollbarRef = React.useRef<HTMLDivElement | null>(null)
  const [hasBrokenScrollbar, setHasBrokenScrollbar] = React.useState(false)

  const answerTimestampInMs =
    callInstance && !callInstance.is_missed && callInstance.first_answered_at
      ? getTimeDifferenceInMs(
          new Date(callInstance.first_answered_at),
          new Date(callInstance.started_at),
        )
      : undefined

  // Add this effect to check for scrollbar
  React.useEffect(() => {
    const checkForScrollbar = () => {
      const userAgent = navigator.userAgent
      const isChrome = userAgent.match(/Chrome/i) !== null

      if (scrollbarRef.current && isChrome) {
        const hasVerticalScrollbar =
          scrollbarRef.current.scrollHeight > scrollbarRef.current.clientHeight
        setHasBrokenScrollbar(hasVerticalScrollbar)
      }
    }

    checkForScrollbar()
    // Add resize listener to check again if window size changes
    window.addEventListener('resize', checkForScrollbar)
    return () => window.removeEventListener('resize', checkForScrollbar)
  }, [callInstance]) // Re-run when transcript data changes

  const handlePlaybackTimestampChange = (timestamp: number) => {
    playbackTimestampRef.current = timestamp
  }

  const voicemailId = callInstance.voicemail_id
  const {
    data: voicemail,
    error: voicemailError,
    isLoading: isVoicemailLoading,
  } = useGetVoicemail({
    voicemailId: voicemailId || 0,
    options: { enabled: !!voicemailId },
  })

  const panelPadding = hasBrokenScrollbar
    ? '1.25rem 0.65rem 1.25rem 1.25rem'
    : '1.25rem'

  return (
    <Flex
      flexDirection="column"
      flex={isModal ? 'none' : '1'}
      overflow={isModal ? 'visible' : 'hidden'}
    >
      <Flex
        flex="0 1 auto"
        flexDirection="column"
        padding={callInstance?.ended_at ? '1.25rem' : '0'}
        paddingBottom="0"
        gap="1.25rem"
      >
        <Flex
          margin="0 -20px 0 -20px"
          borderBottom="solid 2px"
          borderBottomColor="border.muted"
          justifyContent="center"
          alignItems="center"
        >
          {callInstance?.ended_at ? (
            <AudioPlayer
              showDownloadButton={false}
              showInfoButton={false}
              showJumpToAnswerButton={false}
              showInlineInfoButton={true}
              variant={AudioPlayerVariant.Recording}
              audioUri={callInstance!.recording_url}
              answerTimestamp={answerTimestampInMs}
              requestedTimestamp={requestedTimestamp}
              onValueChanged={handlePlaybackTimestampChange}
            />
          ) : (
            <Flex
              alignItems="center"
              justifyContent="center"
              height="8.5rem"
              width="100%"
            >
              <Flex alignItems="center" gap="8px">
                <BouncingEllipsis
                  styles={{
                    ellipsis: {
                      textStyle: 'Body1_Bold',
                      animation: `${bounceAnimation} 2s linear infinite`,
                    },
                  }}
                />
                <Flex
                  {...textStyles[textStyleKeys.Body1_Bold]}
                  color="fg.default"
                >
                  {t('callDetailsExtras.callInProgress', 'Call in progress')}
                </Flex>
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
      <Flex
        flex={isModal ? 'none' : '1'}
        flexDir={'column'}
        overflowY={isModal ? 'visible' : 'auto'}
        minHeight="0"
        ref={scrollbarRef}
      >
        <Flex width="100%" padding={panelPadding}>
          <CallDetailsOverviewTab
            callInstance={callInstance}
            margoSummary={margoSummary}
            answerTimestampInMs={answerTimestampInMs}
            voicemail={voicemail}
            voicemailError={voicemailError ?? undefined}
            isVoicemailLoading={isVoicemailLoading}
          />
        </Flex>
      </Flex>
    </Flex>
  )
}
